import React, { ReactNode } from "react";
import { cn, handleFocus } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Combobox } from "@/components/ui/combobox";
import { DatePicker } from "@/components/ui/typeable_date_picker";
import { Checkbox } from "@/components/ui/checkbox";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { CircleHelp as CircleHelpIcon } from "lucide-react";
import { ErrorKind, ResolvedFieldProps, TooltipConfig } from "./types";

// ---------------------------------------------------------------------------
// Shared label
// ---------------------------------------------------------------------------

function FieldLabel({
  name,
  label,
  required,
  tooltip,
  extra,
  hasRequiredError,
}: {
  name: string;
  label: string;
  required: boolean;
  tooltip?: TooltipConfig;
  extra?: ReactNode;
  /** True when the field has a "required" error that should be shown */
  hasRequiredError?: boolean;
}) {
  return (
    <Label
      htmlFor={name}
      className="flex flex-row justify-between text-base items-center gap-1"
    >
      <span className="font-semibold">{label}</span>
      {tooltip && (
        <HoverCard>
          <HoverCardTrigger asChild>
            <CircleHelpIcon className="h-4 w-4 cursor-help text-muted-foreground" />
          </HoverCardTrigger>
          <HoverCardContent className="w-80">{tooltip.content}</HoverCardContent>
        </HoverCard>
      )}
      {extra}
      {required ? (
        <span
          data-testid={`form-required-${name}`}
          className={cn(
            "text-xs font-light ml-auto",
            hasRequiredError ? "text-red-500 font-medium" : "",
          )}
          style={{ fontStyle: "italic" }}
        >
          Required
        </span>
      ) : (
        <span />
      )}
    </Label>
  );
}

// ---------------------------------------------------------------------------
// Error message
// ---------------------------------------------------------------------------

function FieldError({ error, show, errorKind }: { error?: string; show: boolean; errorKind?: ErrorKind }) {
  // "required" errors are shown via the red Required label + border only — no text below
  if (!show || !error || errorKind === "required") return null;
  return <span className="text-red-500 text-xs mt-0.5">{error}</span>;
}

function errorRingClass(showError: boolean): string {
  return showError
    ? "ring-1 ring-red-500 focus:ring-red-500 border-red-500"
    : "";
}

/** Whether the error is a "required" kind and should be shown */
function hasRequiredError(props: ResolvedFieldProps): boolean {
  return props.showError && props.errorKind === "required";
}

// ---------------------------------------------------------------------------
// TEXT
// ---------------------------------------------------------------------------

function FormTextField({
  props,
  onChange,
  onBlur,
}: {
  props: ResolvedFieldProps;
  onChange: (value: string) => void;
  onBlur?: () => void;
}) {
  return (
    <div className={cn("grid items-center gap-1.5", props.className)} data-testid={`form-field-${props.name}`} data-field-error={props.showError ? "true" : undefined} data-field-error-kind={props.showError ? props.errorKind : undefined}>
      <FieldLabel name={props.name} label={props.label} required={props.required} tooltip={props.tooltip} hasRequiredError={hasRequiredError(props)} />
      <FieldError error={props.error} show={props.showError} errorKind={props.errorKind} />
      <Input
        id={props.name}
        data-testid={`form-input-${props.name}`}
        type="text"
        disabled={props.disabled}
        placeholder={props.placeholder}
        value={(props.value as string) ?? ""}
        onChange={(e) => {
          const val = e.target.value;
          onChange(val === "" ? (undefined as unknown as string) : val);
        }}
        onBlur={onBlur}
        onFocus={handleFocus}
        className={cn(
          "block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:leading-6",
          errorRingClass(props.showError),
        )}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// FLOAT – stores raw string so typing decimals is never mangled
// ---------------------------------------------------------------------------

function FormFloatField({
  props,
  onChange,
  onBlur,
  extra,
}: {
  props: ResolvedFieldProps;
  onChange: (value: string) => void;
  onBlur?: () => void;
  extra?: ReactNode;
}) {
  const displayValue = props.value != null ? `${props.value}` : "";

  return (
    <div className={cn("grid items-center gap-1.5", props.className)} data-testid={`form-field-${props.name}`} data-field-error={props.showError ? "true" : undefined} data-field-error-kind={props.showError ? props.errorKind : undefined}>
      <FieldLabel name={props.name} label={props.label} required={props.required} tooltip={props.tooltip} extra={extra} hasRequiredError={hasRequiredError(props)} />
      <FieldError error={props.error} show={props.showError} errorKind={props.errorKind} />
      <Input
        id={props.name}
        data-testid={`form-input-${props.name}`}
        type="text"
        disabled={props.disabled}
        placeholder={props.placeholder}
        step={props.step}
        value={displayValue}
        onChange={(e) => {
          const cleaned = e.target.value.replace(/[^\-0-9.\$() ]+/gi, "");
          onChange(cleaned === "" ? (undefined as unknown as string) : cleaned);
        }}
        onBlur={onBlur}
        onFocus={handleFocus}
        className={cn(
          "block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:leading-6",
          errorRingClass(props.showError),
        )}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// INT
// ---------------------------------------------------------------------------

function FormIntField({
  props,
  onChange,
  onBlur,
}: {
  props: ResolvedFieldProps;
  onChange: (value: number | undefined) => void;
  onBlur?: () => void;
}) {
  return (
    <div className={cn("grid items-center gap-1.5", props.className)} data-testid={`form-field-${props.name}`} data-field-error={props.showError ? "true" : undefined} data-field-error-kind={props.showError ? props.errorKind : undefined}>
      <FieldLabel name={props.name} label={props.label} required={props.required} tooltip={props.tooltip} hasRequiredError={hasRequiredError(props)} />
      <FieldError error={props.error} show={props.showError} errorKind={props.errorKind} />
      <Input
        id={props.name}
        data-testid={`form-input-${props.name}`}
        type="number"
        disabled={props.disabled}
        placeholder={props.placeholder}
        step={props.step}
        min={props.min}
        max={props.max}
        value={props.value != null ? (props.value as number) : ""}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === "") {
            onChange(undefined);
            return;
          }
          const parsed = parseInt(raw, 10);
          onChange(isNaN(parsed) ? undefined : parsed);
        }}
        onBlur={onBlur}
        onFocus={handleFocus}
        className={cn(
          "block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:leading-6",
          errorRingClass(props.showError),
        )}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// SELECT (Combobox)
// ---------------------------------------------------------------------------

function FormSelectField({
  props,
  onChange,
}: {
  props: ResolvedFieldProps;
  onChange: (value: string) => void;
}) {
  return (
    <div className={cn("grid items-center gap-1.5", props.className)} data-testid={`form-field-${props.name}`} data-field-error={props.showError ? "true" : undefined} data-field-error-kind={props.showError ? props.errorKind : undefined}>
      <FieldLabel name={props.name} label={props.label} required={props.required} tooltip={props.tooltip} hasRequiredError={hasRequiredError(props)} />
      <FieldError error={props.error} show={props.showError} errorKind={props.errorKind} />
      <Combobox
        dwimOnChange
        isModal
        id={props.name}
        className={cn("w-full", errorRingClass(props.showError))}
        disabled={props.disabled}
        value={(props.value as string) ?? ""}
        onValueChange={(item: string) => {
          if (
            props.value &&
            item?.toLocaleLowerCase() === (props.value as string)?.toLocaleLowerCase()
          ) {
            onChange(undefined as unknown as string);
            return;
          }
          // Combobox returns "" on deselect with dwimOnChange — normalize to undefined
          if (item === "") {
            onChange(undefined as unknown as string);
            return;
          }
          onChange(item);
        }}
        options={props.options}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// DATE
// ---------------------------------------------------------------------------

function FormDateField({
  props,
  onChange,
  extra,
}: {
  props: ResolvedFieldProps;
  onChange: (value: Date | undefined) => void;
  extra?: ReactNode;
}) {
  return (
    <div className={cn("grid items-center gap-1.5", props.className)} data-testid={`form-field-${props.name}`} data-field-error={props.showError ? "true" : undefined} data-field-error-kind={props.showError ? props.errorKind : undefined}>
      <FieldLabel name={props.name} label={props.label} required={props.required} tooltip={props.tooltip} extra={extra} hasRequiredError={hasRequiredError(props)} />
      <FieldError error={props.error} show={props.showError} errorKind={props.errorKind} />
      <DatePicker
        id={props.name}
        disabled={props.disabled}
        value={props.value as Date | undefined}
        onChange={(date) => onChange(date)}
        placeholder="MM/DD/YYYY"
        className={cn(
          "rounded-md border-0 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:leading-6 w-full",
          errorRingClass(props.showError),
        )}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// CHECKBOX
// ---------------------------------------------------------------------------

function FormCheckboxField({
  props,
  onChange,
}: {
  props: ResolvedFieldProps;
  onChange: (value: boolean) => void;
}) {
  return (
    <div className={cn("grid items-center gap-1.5", props.className)} data-testid={`form-field-${props.name}`} data-field-error={props.showError ? "true" : undefined} data-field-error-kind={props.showError ? props.errorKind : undefined}>
      <FieldLabel name={props.name} label={props.label} required={props.required} tooltip={props.tooltip} hasRequiredError={hasRequiredError(props)} />
      <FieldError error={props.error} show={props.showError} errorKind={props.errorKind} />
      <Checkbox
        id={props.name}
        disabled={props.disabled}
        checked={(props.value as boolean) ?? false}
        onCheckedChange={(checked) => onChange(!!checked)}
        className={cn(
          "h-6 w-6 block rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600",
          errorRingClass(props.showError),
        )}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Auto-render based on widget type
// ---------------------------------------------------------------------------

export function FormField({
  fieldProps,
  onChange,
  onBlur,
  extra,
}: {
  fieldProps: ResolvedFieldProps;
  onChange: (value: unknown) => void;
  onBlur?: () => void;
  extra?: ReactNode;
}) {
  if (!fieldProps.visible) return null;

  switch (fieldProps.widget) {
    case "text":
      return <FormTextField props={fieldProps} onChange={(v) => onChange(v)} onBlur={onBlur} />;
    case "float":
      return <FormFloatField props={fieldProps} onChange={(v) => onChange(v)} onBlur={onBlur} extra={extra} />;
    case "int":
      return <FormIntField props={fieldProps} onChange={(v) => onChange(v)} onBlur={onBlur} />;
    case "select":
      return <FormSelectField props={fieldProps} onChange={(v) => onChange(v)} />;
    case "date":
      return <FormDateField props={fieldProps} onChange={(v) => onChange(v)} extra={extra} />;
    case "checkbox":
      return <FormCheckboxField props={fieldProps} onChange={(v) => onChange(v)} />;
    default:
      return null;
  }
}
